import React, {useCallback, useMemo, useState} from "react";
import {Controller, useFormContext, useWatch} from "react-hook-form";
import {Box, Stack} from "@mui/material";
import {ComboBox} from "../../../comboBox/ComboBox.jsx";
import DaapAipSubPrevEdit from "./DaapAipSubPrevEdit.jsx";
import {useDaapExistingSubscriptions, useDaapMetadata} from "../hooks/DaapSubscription.hooks.jsx";
import "react-toastify/dist/ReactToastify.css";
import "../../../../scss/PowerTabs.scss";

const buildSubscriptionId = (email, value) => `${email}-${value}`;

const getOptionValue = (option) => typeof option === "string" ? option : option.value;

const DaapAipSubForm = ({
                            selectedCompany,
                            selectedContactEmails,
                            hasSelectedContacts,
                        }) => {
    const [removedGeoRows, setRemovedGeoRows] = useState(new Map());
    const [removedSubcatRows, setRemovedSubcatRows] = useState(new Map());
    const [ignoredAddedGeoRowIds, setIgnoredAddedGeoRowIds] = useState(new Set());
    const [ignoredAddedSubcatRowIds, setIgnoredAddedSubcatRowIds] = useState(new Set());

    const {
        control,
        reset,
        setValue,
    } = useFormContext();

    const selectedGeographies = useWatch({
        control,
        name: "selectedGeographies",
    }) || [];

    const selectedSubcategories = useWatch({
        control,
        name: "selectedSubcategories",
    }) || [];

    const {data: initialData, loading: isInitialDataLoading} = useDaapExistingSubscriptions(selectedContactEmails);
    const {data: metadata, loading: isMetadataLoading} = useDaapMetadata();

    const geographies = useMemo(() => {
        return metadata?.sdnoDaapGeographies?.map((geo) => ({
            value: geo.name,
            displayName: geo.name,
        })) || [];
    }, [metadata]);

    const subcategories = useMemo(() => {
        return metadata?.sdnoDaapSubcategories?.map((subcat) => ({
            value: subcat.name,
            displayName: subcat.name,
        })) || [];
    }, [metadata]);

    const initialGeoRows = useMemo(() => {
        const selectedContactEmailSet = new Set(selectedContactEmails);

        return (initialData?.sdnoDaapSubscriptions?.geographies || []).map((subscription) => ({
            id: buildSubscriptionId(subscription.email, subscription.geography),
            email: subscription.email,
            geography: subscription.geography,
        })).filter((row) => selectedContactEmailSet.has(row.email));
    }, [initialData, selectedContactEmails]);

    const initialSubcatRows = useMemo(() => {
        const selectedContactEmailSet = new Set(selectedContactEmails);

        return (initialData?.sdnoDaapSubscriptions?.subcategories || []).map((subscription) => ({
            id: buildSubscriptionId(subscription.email, subscription.subcategory),
            email: subscription.email,
            subcategory: subscription.subcategory,
        })).filter((row) => selectedContactEmailSet.has(row.email));
    }, [initialData, selectedContactEmails]);

    const resetDraftState = useCallback(() => {
        setRemovedGeoRows(new Map());
        setRemovedSubcatRows(new Map());
        setIgnoredAddedGeoRowIds(new Set());
        setIgnoredAddedSubcatRowIds(new Set());
    }, []);

    const deleteGeo = useCallback((row) => {
        if (row.isNew) {
            setIgnoredAddedGeoRowIds((prev) => {
                const next = new Set(prev);
                next.add(row.id);

                return next;
            });
            return;
        }

        setRemovedGeoRows((prev) => {
            const next = new Map(prev);

            if (next.has(row.id)) {
                next.delete(row.id);
            } else {
                next.set(row.id, row);
            }

            return next;
        });
    }, []);

    const deleteSubcat = useCallback((row) => {
        if (row.isNew) {
            setIgnoredAddedSubcatRowIds((prev) => {
                const next = new Set(prev);
                next.add(row.id);

                return next;
            });
            return;
        }

        setRemovedSubcatRows((prev) => {
            const next = new Map(prev);

            if (next.has(row.id)) {
                next.delete(row.id);
            } else {
                next.set(row.id, row);
            }

            return next;
        });
    }, []);

    const geoDraft = useMemo(() => {
        const added = new Map();
        const initialGeoRowIds = new Set(initialGeoRows.map((row) => row.id));

        selectedContactEmails.forEach((email) => {
            selectedGeographies.forEach((geo) => {
                const geography = getOptionValue(geo);
                const id = buildSubscriptionId(email, geography);

                if (initialGeoRowIds.has(id) || removedGeoRows.has(id) || ignoredAddedGeoRowIds.has(id)) {
                    return;
                }

                added.set(id, {
                    id,
                    email,
                    geography,
                    isNew: true,
                });
            });
        });

        return {
            added,
            removed: removedGeoRows,
        };
    }, [ignoredAddedGeoRowIds, initialGeoRows, removedGeoRows, selectedContactEmails, selectedGeographies]);

    const subcatDraft = useMemo(() => {
        const added = new Map();
        const initialSubcatRowIds = new Set(initialSubcatRows.map((row) => row.id));

        selectedContactEmails.forEach((email) => {
            selectedSubcategories.forEach((subcat) => {
                const subcategory = getOptionValue(subcat);
                const id = buildSubscriptionId(email, subcategory);

                if (initialSubcatRowIds.has(id) || removedSubcatRows.has(id) || ignoredAddedSubcatRowIds.has(id)) {
                    return;
                }

                added.set(id, {
                    id,
                    email,
                    subcategory,
                    isNew: true,
                });
            });
        });

        return {
            added,
            removed: removedSubcatRows,
        };
    }, [ignoredAddedSubcatRowIds, initialSubcatRows, removedSubcatRows, selectedContactEmails, selectedSubcategories]);

    const geoDraftRows = useMemo(() => {
        const baseRows = initialGeoRows.map((row) => ({
            ...row,
            isMarkedForDeletion: geoDraft.removed.has(row.id),
        }));

        return [
            ...baseRows,
            ...Array.from(geoDraft.added.values()),
        ];
    }, [initialGeoRows, geoDraft]);

    const subcatDraftRows = useMemo(() => {
        const baseRows = initialSubcatRows.map((row) => ({
            ...row,
            isMarkedForDeletion: subcatDraft.removed.has(row.id),
        }));

        return [
            ...baseRows,
            ...Array.from(subcatDraft.added.values()),
        ];
    }, [initialSubcatRows, subcatDraft]);

    const resetAfterSave = useCallback(() => {
        setValue("selectedGeographies", []);
        setValue("selectedSubcategories", []);
        reset();
        resetDraftState();
    }, [reset, resetDraftState, setValue]);

    return (
        <>
            <Stack spacing={3}>
                <Stack direction="row" spacing={2} sx={{width: "100%"}}>
                    <Box sx={{flex: 1}}>
                        <Controller
                            name="selectedGeographies"
                            control={control}
                            render={({field}) => (
                                <ComboBox
                                    label="Geographies *"
                                    isMultiple
                                    isLoading={isMetadataLoading}
                                    disabled={!hasSelectedContacts}
                                    data={geographies}
                                    style={{width: "100%"}}
                                    value={field.value}
                                    onChange={(_, values) => {
                                        field.onChange(values || []);
                                    }}
                                />
                            )}
                        />
                    </Box>

                    <Box sx={{flex: 1}}>
                        <Controller
                            name="selectedSubcategories"
                            control={control}
                            render={({field}) => (
                                <ComboBox
                                    label="Subcategories *"
                                    isMultiple
                                    isLoading={isMetadataLoading}
                                    disabled={!hasSelectedContacts}
                                    data={subcategories}
                                    style={{width: "100%"}}
                                    value={field.value}
                                    onChange={(_, values) => {
                                        field.onChange(values || []);
                                    }}
                                />
                            )}
                        />
                    </Box>
                </Stack>
            </Stack>

            <DaapAipSubPrevEdit
                selectedCompany={selectedCompany}
                selectedContactEmails={selectedContactEmails}
                geoDraft={geoDraft}
                subcatDraft={subcatDraft}
                geoDraftRows={geoDraftRows}
                subcatDraftRows={subcatDraftRows}
                deleteGeo={deleteGeo}
                deleteSubcat={deleteSubcat}
                isInitialDataLoading={isInitialDataLoading}
                resetForm={resetAfterSave}
            />
        </>
    );
};

export default DaapAipSubForm;
